import { DatePicker, Form, Input, Select } from "antd";
import moment from "moment";
import { useSearchParams, useSubmit } from "react-router-dom";
import { useRouteActivity } from "~/hooks/useRouteActivity";
import { useTranslation } from "react-i18next";
import {
  ResponsiveFilterChip,
  ResponsiveFilterGrid,
  ResponsiveFilterPanel,
  ReviewStatusTag,
  useFilterAutoSubmitOnChange,
} from "~/components";
import {
  removeEmptyFields,
  SERVICE_REQUEST_STATUS_TYPES,
  SERVICE_REQUEST_TYPES,
} from "~/utils";
import * as APITypes from "~/API";

export const ServiceRequestTableFilters = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const initialValues = {
    status:
      (searchParams.get("status") as APITypes.ServiceRequestStatusType) ||
      undefined,
    type:
      (searchParams.get("type") as APITypes.ServiceRequestType) || undefined,
    title: searchParams.get("title") || undefined,
    handlerName: searchParams.get("handlerName") || undefined,
    tenantName: searchParams.get("tenantName") || undefined,
    contractor: searchParams.get("contractor") || undefined,
    createdAtIntervalStart: searchParams.get("createdAtIntervalStart")
      ? moment(searchParams.get("createdAtIntervalStart"), "YYYY-MM-DD")
      : undefined,
    createdAtIntervalEnd: searchParams.get("createdAtIntervalEnd")
      ? moment(searchParams.get("createdAtIntervalEnd"), "YYYY-MM-DD")
      : undefined,
  };

  const [formInstance] = Form.useForm();

  const submit = useSubmit();
  const { isTableLoading: isLoading } = useRouteActivity();
  const submitOnChange = useFilterAutoSubmitOnChange(formInstance);

  const handleSubmit = () => {
    const formValues = removeEmptyFields(formInstance.getFieldsValue(true));

    // Format date values to ISO string format for API
    if (formValues.createdAtIntervalStart) {
      const date = formValues.createdAtIntervalStart;
      formValues.createdAtIntervalStart = date.format("YYYY-MM-DD");
    }

    if (formValues.createdAtIntervalEnd) {
      const date = formValues.createdAtIntervalEnd;
      formValues.createdAtIntervalEnd = date.format("YYYY-MM-DD");
    }

    submit(formValues, { method: "get" });
  };

  const handleClear = () => {
    formInstance.resetFields();
    submit({}, { method: "get" });
  };

  const activeFilters: ResponsiveFilterChip[] = [];

  if (initialValues.status) {
    activeFilters.push({
      key: "status",
      label: t("Status"),
      value: String(initialValues.status),
    });
  }

  if (initialValues.type) {
    activeFilters.push({
      key: "type",
      label: t("Type"),
      value: String(initialValues.type),
    });
  }

  if (initialValues.title) {
    activeFilters.push({
      key: "title",
      label: t("Title"),
      value: initialValues.title,
    });
  }

  if (initialValues.handlerName) {
    activeFilters.push({
      key: "handlerName",
      label: t("Handler"),
      value: initialValues.handlerName,
    });
  }

  if (initialValues.tenantName) {
    activeFilters.push({
      key: "tenantName",
      label: t("Tenant"),
      value: initialValues.tenantName,
    });
  }

  if (initialValues.contractor) {
    activeFilters.push({
      key: "contractor",
      label: t("Contractor"),
      value: initialValues.contractor,
    });
  }

  if (searchParams.get("createdAtIntervalStart")) {
    activeFilters.push({
      key: "createdAtIntervalStart",
      label: t("Created_At_Start"),
      value: searchParams.get("createdAtIntervalStart") || "",
    });
  }

  if (searchParams.get("createdAtIntervalEnd")) {
    activeFilters.push({
      key: "createdAtIntervalEnd",
      label: t("Created_At_End"),
      value: searchParams.get("createdAtIntervalEnd") || "",
    });
  }

  return (
    <ResponsiveFilterPanel
      title={t("Filters")}
      activeFilters={activeFilters}
      isLoading={isLoading}
      desktopCollapseKey="request-table-filter-collapse"
      onApply={() => formInstance.submit()}
      onClear={handleClear}
    >
      <Form
        form={formInstance}
        layout="vertical"
        initialValues={initialValues}
        onFinish={handleSubmit}
        key={searchParams.toString()}
      >
        <ResponsiveFilterGrid>
          <div data-tour="sr-status-filter">
            <Form.Item label={t("Status")} name="status">
              <Select
                placeholder={t("Select_a_status")}
                onChange={submitOnChange}
                allowClear
                disabled={isLoading}
              >
                {SERVICE_REQUEST_STATUS_TYPES.map((status) => {
                  return (
                    <Select.Option key={status} value={status}>
                      <ReviewStatusTag status={status} />
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          </div>

          <Form.Item label={t("Type")} name="type">
            <Select
              placeholder={t("Select_a_request_type")}
              onChange={submitOnChange}
              allowClear
              disabled={isLoading}
            >
              {SERVICE_REQUEST_TYPES.map((type) => {
                return (
                  <Select.Option key={type} value={type}>
                    {t(type as any)}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item label={t("Title")} name="title">
            <Input
              placeholder={t("Enter_a_title")}
              disabled={isLoading}
              allowClear
              onPressEnter={() => formInstance.submit()}
            />
          </Form.Item>

          <Form.Item label={t("Handler")} name="handlerName">
            <Input
              placeholder={t("Enter_a_handler_name")}
              disabled={isLoading}
              allowClear
              onPressEnter={() => formInstance.submit()}
            />
          </Form.Item>

          <Form.Item label={t("Tenant")} name="tenantName">
            <Input
              placeholder={t("Enter_a_tenant_name")}
              disabled={isLoading}
              allowClear
              onPressEnter={() => formInstance.submit()}
            />
          </Form.Item>

          <Form.Item label={t("Contractor")} name="contractor">
            <Input
              placeholder={t("Enter_a_contractor")}
              disabled={isLoading}
              allowClear
              onPressEnter={() => formInstance.submit()}
            />
          </Form.Item>

          <Form.Item
            label={t("Created_At_Start")}
            name="createdAtIntervalStart"
          >
            <DatePicker
              format="MM-DD-YYYY"
              placeholder={t("Start_Date")}
              disabled={isLoading}
              placement="bottomLeft"
              onChange={submitOnChange}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label={t("Created_At_End")}
            name="createdAtIntervalEnd"
          >
            <DatePicker
              format="MM-DD-YYYY"
              placeholder={t("End_Date")}
              disabled={isLoading}
              placement="bottomRight"
              onChange={submitOnChange}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </ResponsiveFilterGrid>
      </Form>
    </ResponsiveFilterPanel>
  );
};
